import mixpanel from 'mixpanel-browser';
import { OpenPanel } from '@openpanel/web';
import { dispatchPaymentAnalytics } from './paymentAnalyticsContext';
import { isMerchantImpersonationActive, isMerchantImpersonationEntry } from './merchantSession';

export type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

export const ANALYTICS_EVENTS = {
    PAGE_VIEWED: 'frontend_page_viewed',
    USER_IDENTIFIED: 'frontend_user_identified',
    AUTH_SUCCEEDED: 'frontend_auth_succeeded',
    AUTH_FAILED: 'frontend_auth_failed',
    PAYMENT_LINK_OPENED: 'payment_link_opened',
    PAYMENT_LINK_LOADED: 'payment_link_loaded',
    PAYMENT_FORM_VIEWED: 'payment_form_viewed',
    PAYMENT_LINK_LOAD_FAILED: 'payment_link_load_failed',
    PAYMENT_ACTION_CLICKED: 'payment_action_clicked',
    PAYMENT_PAGE_HIDDEN: 'payment_page_hidden',
    PAYMENT_PAGE_VISIBLE: 'payment_page_visible',
    PAYMENT_PAGE_LEFT: 'payment_page_left',
    PAYMENT_PAGE_RESTORED: 'payment_page_restored',
    PAYMENT_STATUS_CHANGED: 'payment_status_changed_frontend',
    PAYMENT_REQUISITE_COPIED: 'payment_requisite_copied',
    PAYMENT_BANK_OPEN_CLICKED: 'payment_bank_open_clicked',
    PAYMENT_TRANSFER_CONFIRMED: 'payment_transfer_confirmed_frontend',
    PAYMENT_TRANSFER_CONFIRM_FAILED: 'payment_transfer_confirm_failed',
    PAYMENT_USER_REQUISITES_SUBMITTED: 'payment_user_requisites_submitted',
    PAYMENT_USER_REQUISITES_SUBMIT_FAILED: 'payment_user_requisites_submit_failed',
    PAYMENT_RECEIPT_CONFIRMED: 'payment_receipt_confirmed_frontend',
    PAYMENT_RECEIPT_CONFIRM_FAILED: 'payment_receipt_confirm_failed',
    PAYMENT_CANCELLED_BY_USER: 'payment_cancelled_by_user_frontend',
    PAYMENT_CANCEL_FAILED: 'payment_cancel_failed',
    MERCHANT_PAYMENT_CREATED: 'merchant_payment_created_frontend',
    MERCHANT_PAYMENT_CREATE_FAILED: 'merchant_payment_create_failed',
    MERCHANT_PAYMENT_LINK_COPIED: 'merchant_payment_link_copied',
    MERCHANT_PAYMENT_LINK_OPEN_CLICKED: 'merchant_payment_link_open_clicked',
    SANDBOX_TRANSACTION_CREATED: 'sandbox_transaction_created_frontend',
    SANDBOX_TRANSACTION_CREATE_FAILED: 'sandbox_transaction_create_failed',
    SANDBOX_STATUS_SIMULATED: 'sandbox_status_simulated_frontend',
    SANDBOX_STATUS_SIMULATE_FAILED: 'sandbox_status_simulate_failed',
} as const;

type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

interface AnalyticsUser {
    id?: string;
    username?: string;
    email?: string;
    company_name?: string;
    user_type?: string;
}

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN?.trim();
const MIXPANEL_API_HOST = process.env.REACT_APP_MIXPANEL_API_HOST?.trim();
const OPENPANEL_API_URL = process.env.REACT_APP_OPENPANEL_API_URL?.trim();
const OPENPANEL_CLIENT_ID = process.env.REACT_APP_OPENPANEL_CLIENT_ID?.trim();
const DEBUG_ENABLED = process.env.REACT_APP_DEBUG === 'true';

let mixpanelInitialized = false;
let openPanel: OpenPanel | null = null;
let missingMixpanelTokenLogged = false;
let missingOpenPanelConfigLogged = false;
let lastPageKey = '';
let lastPageTrackedAt = 0;
const trackedOnceKeys = new Set<string>();

const sanitizeProperties = (properties: AnalyticsProperties = {}) => {
    return Object.entries(properties).reduce<Record<string, string | number | boolean | null>>((acc, [key, value]) => {
        if (value !== undefined) {
            acc[key] = value;
        }
        return acc;
    }, {});
};

const getStaticProperties = (): AnalyticsProperties => ({
    app: 'payment_frontend',
    deployment: process.env.REACT_APP_DEPLOYMENT === 'true',
});

const getCommonProperties = (): AnalyticsProperties => {
    if (typeof window === 'undefined') {
        return getStaticProperties();
    }

    return {
        ...getStaticProperties(),
        page_path: window.location.pathname,
        page_search: window.location.search || null,
        page_url: window.location.href,
    };
};

export const isAnalyticsEnabled = () => Boolean(
    MIXPANEL_TOKEN || (OPENPANEL_API_URL && OPENPANEL_CLIENT_ID)
);

export const initAnalytics = () => {
    // Neither identify as the merchant nor leak the handoff fragment to analytics.
    if (isMerchantImpersonationEntry() || isMerchantImpersonationActive()) return false;
    if (!MIXPANEL_TOKEN) {
        if (DEBUG_ENABLED && !missingMixpanelTokenLogged) {
            console.info('Mixpanel frontend disabled: REACT_APP_MIXPANEL_TOKEN is not set');
            missingMixpanelTokenLogged = true;
        }
    } else if (!mixpanelInitialized) {
        try {
            const config = {
                debug: DEBUG_ENABLED,
                persistence: 'localStorage' as const,
                ...(MIXPANEL_API_HOST ? { api_host: MIXPANEL_API_HOST } : {}),
            };

            mixpanel.init(MIXPANEL_TOKEN, config);
            mixpanel.register(sanitizeProperties(getStaticProperties()));
            mixpanelInitialized = true;
        } catch (error) {
            if (DEBUG_ENABLED) {
                console.error('Mixpanel init error:', error);
            }
        }
    }

    if (!OPENPANEL_API_URL || !OPENPANEL_CLIENT_ID) {
        if (DEBUG_ENABLED && !missingOpenPanelConfigLogged) {
            console.info(
                'OpenPanel frontend disabled: REACT_APP_OPENPANEL_API_URL or REACT_APP_OPENPANEL_CLIENT_ID is not set'
            );
            missingOpenPanelConfigLogged = true;
        }
    } else if (!openPanel) {
        try {
            openPanel = new OpenPanel({
                apiUrl: OPENPANEL_API_URL,
                clientId: OPENPANEL_CLIENT_ID,
                debug: DEBUG_ENABLED,
                trackScreenViews: false,
                trackOutgoingLinks: false,
                trackAttributes: false,
                trackHashChanges: false,
                sessionReplay: { enabled: false },
            });
            openPanel.setGlobalProperties(sanitizeProperties(getStaticProperties()));
        } catch (error) {
            if (DEBUG_ENABLED) {
                console.error('OpenPanel init error:', error);
            }
        }
    }

    return mixpanelInitialized || Boolean(openPanel);
};

export const trackAnalyticsEvent = (
    eventName: AnalyticsEventName,
    properties: AnalyticsProperties = {},
    options: { flushImmediately?: boolean } = {}
) => {
    dispatchPaymentAnalytics({ ...getCommonProperties(), ...properties }, (enriched, immediate) => {
        sendAnalyticsEvent(eventName, enriched, { flushImmediately: immediate });
    }, options.flushImmediately);
};

const sendAnalyticsEvent = (
    eventName: AnalyticsEventName,
    properties: AnalyticsProperties,
    options: { flushImmediately?: boolean },
) => {
    if (!initAnalytics()) {
        return;
    }

    const eventProperties = sanitizeProperties({
        ...getCommonProperties(),
        ...properties,
    });

    if (mixpanelInitialized) {
        try {
            if (options.flushImmediately) {
                // Do not leave lifecycle events in the batch queue as the browser
                // may suspend or discard this document immediately afterwards.
                mixpanel.track(eventName, eventProperties, { transport: 'sendBeacon', send_immediately: true });
            } else {
                mixpanel.track(eventName, eventProperties);
            }
        } catch (error) {
            if (DEBUG_ENABLED) {
                console.error('Mixpanel track error:', error);
            }
        }
    }

    if (openPanel) {
        try {
            // The web SDK derives every event's __path from its last screenView.
            // Sync here as payment effects can run before AnalyticsRouteTracker.
            // screenView deduplicates the URL; automatic tracking stays disabled
            // so impersonation routes still pass through our guard above.
            if (typeof window !== 'undefined') {
                openPanel.screenView(String(eventProperties.page_url || window.location.href), eventProperties);
            }
            // OpenPanel's SDK already sends each event with fetch keepalive.
            void openPanel.track(eventName, eventProperties).catch((error) => {
                if (DEBUG_ENABLED) {
                    console.error('OpenPanel track error:', error);
                }
            });
        } catch (error) {
            if (DEBUG_ENABLED) {
                console.error('OpenPanel track error:', error);
            }
        }
    }
};

export const trackAnalyticsEventOnce = (
    eventName: AnalyticsEventName,
    onceKey: string,
    properties: AnalyticsProperties = {}
) => {
    const key = `${eventName}:${onceKey}`;
    if (trackedOnceKeys.has(key)) {
        return;
    }

    trackedOnceKeys.add(key);
    trackAnalyticsEvent(eventName, properties);
};

export const trackPageView = (properties: AnalyticsProperties = {}) => {
    if (typeof window === 'undefined' || !initAnalytics()) {
        return;
    }

    const now = Date.now();
    const pageKey = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (pageKey === lastPageKey && now - lastPageTrackedAt < 1000) {
        return;
    }

    lastPageKey = pageKey;
    lastPageTrackedAt = now;

    trackAnalyticsEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
        page_hash: window.location.hash || null,
        ...properties,
    });
};

export const identifyAnalyticsUser = (userType: string, user: AnalyticsUser | null | undefined) => {
    if (!user?.id || !initAnalytics()) {
        return;
    }

    const distinctId = `${userType}:${user.id}`;

    const commonUserProperties = sanitizeProperties({
        user_id: user.id,
        user_type: userType,
        trader_id: userType === 'trader' ? user.id : null,
        trader_username: userType === 'trader' ? user.username ?? null : null,
        merchant_id: userType === 'merchant' ? user.id : null,
        merchant_username: userType === 'merchant' ? user.username ?? null : null,
    });
    const profileProperties = sanitizeProperties({
        username: user.username,
        company_name: user.company_name,
        backend_user_type: user.user_type,
    });

    if (mixpanelInitialized) {
        try {
            mixpanel.identify(distinctId);
            mixpanel.register(commonUserProperties);
            mixpanel.people.set(sanitizeProperties({
                $email: user.email,
                $name: user.username,
                ...commonUserProperties,
                ...profileProperties,
            }));
        } catch (error) {
            if (DEBUG_ENABLED) {
                console.error('Mixpanel identify error:', error);
            }
        }
    }

    if (openPanel) {
        try {
            openPanel.setGlobalProperties({
                ...sanitizeProperties(getStaticProperties()),
                ...commonUserProperties,
            });
            const identifyRequest = openPanel.identify({
                profileId: distinctId,
                email: user.email,
                firstName: user.username,
                properties: {
                    ...commonUserProperties,
                    ...profileProperties,
                },
            });
            if (identifyRequest) {
                void identifyRequest.catch((error) => {
                    if (DEBUG_ENABLED) {
                        console.error('OpenPanel identify error:', error);
                    }
                });
            }
        } catch (error) {
            if (DEBUG_ENABLED) {
                console.error('OpenPanel identify error:', error);
            }
        }
    }

    trackAnalyticsEvent(ANALYTICS_EVENTS.USER_IDENTIFIED, {
        user_id: user.id,
        user_type: userType,
    });
};

export const resetAnalyticsUser = () => {
    if (!initAnalytics()) {
        return;
    }

    if (mixpanelInitialized) {
        try {
            mixpanel.reset();
            mixpanel.register(sanitizeProperties(getStaticProperties()));
        } catch (error) {
            if (DEBUG_ENABLED) {
                console.error('Mixpanel reset error:', error);
            }
        }
    }

    if (openPanel) {
        try {
            openPanel.clear();
            openPanel.setGlobalProperties(sanitizeProperties({
                ...getStaticProperties(),
                trader_id: null,
                trader_username: null,
                merchant_id: null,
                merchant_username: null,
            }));
        } catch (error) {
            if (DEBUG_ENABLED) {
                console.error('OpenPanel clear error:', error);
            }
        }
    }
};
